import React, { useState, useEffect } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';
import { useI18n } from '../i18n/i18n';
import { api } from '../lib/api';
import MonthlyCostWidget from './MonthlyCostWidget';
import PowerAnalysisWidget from './PowerAnalysisWidget';
import LocationComplianceWidget from './LocationComplianceWidget';
import EquipmentWidget from './EquipmentWidget';

// Updated PERIOD_OPTIONS without 30d
const PERIOD_OPTIONS = ['4h', '12h', '24h', '3d', '1w', '1M', '2M', '3M', '6M', '12M'] as const;

export type PeriodOption = (typeof PERIOD_OPTIONS)[number];

interface AnalyticsContainerProps {
  className?: string;
}

interface FilterValues {
  rack_ids: string[];
  locations: string[];
}

const AnalyticsContainer: React.FC<AnalyticsContainerProps> = ({ className = '' }) => {
  const { t } = useI18n();
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>('24h');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRack, setSelectedRack] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [filterValues, setFilterValues] = useState<FilterValues>({ rack_ids: [], locations: [] });
  const [loadingFilters, setLoadingFilters] = useState(true);

  // Custom date range states
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Load filter values on mount
  useEffect(() => {
    const loadFilterValues = async () => {
      try {
        const response = await api.get<FilterValues>('/api/schneider/filters/values');
        setFilterValues(response.data);
      } catch (error) {
        console.error('Failed to load filter values:', error);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilterValues();
  }, []);

  const handlePeriodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as PeriodOption;
    setSelectedPeriod(value);
  };

  const handleRackChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRack(event.target.value);
  };

  const handleLocationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLocation(event.target.value);
  };

  const handleGlobalRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const periodLabels = (option: PeriodOption): string => {
    switch (option) {
      case '4h': return t('pue.period.4h');
      case '12h': return t('pue.period.12h');
      case '24h': return t('pue.period.24h');
      case '3d': return t('pue.period.3d');
      case '1w': return t('pue.period.1w');
      case '1M': return t('pue.period.1M');
      case '2M': return t('pue.period.2M');
      case '3M': return t('pue.period.3M');
      case '6M': return t('pue.period.6M');
      case '12M': return t('pue.period.12M');
      default: return option;
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Global Header with Period Selector */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('analytics.title')}</h2>
              <p className="text-sm text-gray-500">{t('analytics.subtitle')}</p>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-wrap gap-2">
            {/* Rack Filter */}
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <span className="font-medium">Rack:</span>
              <select
                value={selectedRack}
                onChange={handleRackChange}
                disabled={loadingFilters}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px] disabled:opacity-50"
              >
                <option value="all">All Racks</option>
                {filterValues.rack_ids.map(rack => (
                  <option key={rack} value={rack}>
                    {rack}
                  </option>
                ))}
              </select>
            </label>

            {/* Location Filter */}
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <span className="font-medium">Location:</span>
              <select
                value={selectedLocation}
                onChange={handleLocationChange}
                disabled={loadingFilters}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px] disabled:opacity-50"
              >
                <option value="all">All Locations</option>
                {filterValues.locations.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </label>

            {/* Time Range or Custom Date Range Toggle */}
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={useCustomDateRange}
                onChange={(e) => setUseCustomDateRange(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="font-medium">Custom Dates</span>
            </label>

            {!useCustomDateRange ? (
              /* Time Range Dropdown */
              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <span className="font-medium">{t('pue.period.label')}</span>
                <select
                  value={selectedPeriod}
                  onChange={handlePeriodChange}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
                >
                  {PERIOD_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {periodLabels(option)}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              /* Custom Date Range Inputs */
              <>
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <span className="font-medium">From:</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </label>
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <span className="font-medium">To:</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </label>
              </>
            )}

            <button
              onClick={handleGlobalRefresh}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              title={t('analytics.refreshAll')}
            >
              <RefreshCw className="h-4 w-4" />
              <span>{t('analytics.refreshAll')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Widgets Grid 2x2 */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-1 h-[420px]">
            <MonthlyCostWidget hideHeader={true} />
          </div>
          <div className="bg-gray-50 rounded-lg p-1 h-[420px]">
            <PowerAnalysisWidget
              period={!useCustomDateRange ? selectedPeriod : undefined}
              dateFrom={useCustomDateRange && dateFrom ? dateFrom : undefined}
              dateTo={useCustomDateRange && dateTo ? dateTo : undefined}
              refreshKey={refreshKey}
              hideHeader={true}
              rackId={selectedRack !== 'all' ? selectedRack : undefined}
              location={selectedLocation !== 'all' ? selectedLocation : undefined}
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-1 h-[420px]">
            <EquipmentWidget
              period={!useCustomDateRange ? selectedPeriod : undefined}
              dateFrom={useCustomDateRange && dateFrom ? dateFrom : undefined}
              dateTo={useCustomDateRange && dateTo ? dateTo : undefined}
              refreshKey={refreshKey}
              hideHeader={true}
              rackId={selectedRack !== 'all' ? selectedRack : undefined}
              location={selectedLocation !== 'all' ? selectedLocation : undefined}
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-1 h-[420px]">
            <LocationComplianceWidget
              period={!useCustomDateRange ? selectedPeriod : undefined}
              dateFrom={useCustomDateRange && dateFrom ? dateFrom : undefined}
              dateTo={useCustomDateRange && dateTo ? dateTo : undefined}
              refreshKey={refreshKey}
              hideHeader={true}
              rackId={selectedRack !== 'all' ? selectedRack : undefined}
              location={selectedLocation !== 'all' ? selectedLocation : undefined}
            />
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 rounded-b-lg">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{t('analytics.footer.info')}</span>
          <span>{t('analytics.footer.period')}: {periodLabels(selectedPeriod)}</span>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsContainer;
export { PERIOD_OPTIONS };